import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Plus, Check, X, IndianRupee, TrendingUp, Receipt, Users } from "lucide-react";
import { useAuth, useCanDelete } from "@/context/AuthContext";

const money = (v) => "₹" + Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATUS_STYLES = {
  submitted: "bg-status-pending/15 text-status-pending",
  approved: "bg-status-delivered/15 text-status-delivered",
  reimbursed: "bg-primary/15 text-primary",
  rejected: "bg-status-cancelled/15 text-status-cancelled",
};

const EMPTY = { order_code: "", category: "toll", amount: 0, payment_mode: "cash", remarks: "" };

export default function Expenses() {
  const { user } = useAuth();
  const isPrivileged = ["admin", "accounts", "manager"].includes(user?.role);
  return (
    <div className="space-y-4" data-testid="expenses-page">
      <div>
        <h1 className="font-display text-3xl font-black tracking-tight">Trip Expenses</h1>
        <p className="text-sm text-muted-foreground">Drivers log tolls, food, parking &amp; repairs on the road. Admin / Accounts approve for reimbursement.</p>
      </div>
      <Tabs defaultValue="list">
        <TabsList>
          <TabsTrigger value="list" data-testid="tab-expenses-list"><Receipt size={14} className="mr-1" /> Entries</TabsTrigger>
          {isPrivileged && <TabsTrigger value="report" data-testid="tab-expenses-report"><TrendingUp size={14} className="mr-1" /> Report</TabsTrigger>}
        </TabsList>
        <TabsContent value="list"><ExpensesList /></TabsContent>
        {isPrivileged && <TabsContent value="report"><ExpensesReport /></TabsContent>}
      </Tabs>
    </div>
  );
}

function ExpensesList() {
  const { user } = useAuth();
  const isPrivileged = ["admin", "accounts", "manager"].includes(user?.role);
  const canDelete = useCanDelete();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["expenses"], queryFn: () => api.get("/expenses").then((r) => r.data) });

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [statusFilter, setStatusFilter] = useState("all");

  const create = useMutation({
    mutationFn: () => api.post("/expenses", form),
    onSuccess: () => { toast.success("Expense submitted"); qc.invalidateQueries({ queryKey: ["expenses"] }); setOpen(false); setForm(EMPTY); },
    onError: (e) => toast.error(e.response?.data?.detail || "Save failed"),
  });
  const status = useMutation({
    mutationFn: ({ id, status: st }) => api.patch(`/expenses/${id}/status`, { status: st }),
    onSuccess: () => { toast.success("Status updated"); qc.invalidateQueries({ queryKey: ["expenses"] }); },
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/expenses/${id}`),
    onSuccess: () => { toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["expenses"] }); },
  });

  const items = (data?.items || []).filter((e) => statusFilter === "all" ? true : e.status === statusFilter);

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <CardTitle>All entries</CardTitle>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="max-w-40" data-testid="expense-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="submitted">Submitted</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="reimbursed">Reimbursed</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="btn-new-expense"><Plus size={14} className="mr-1" /> Log expense</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Log a trip expense</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Order code (optional)</Label><Input value={form.order_code} onChange={(e) => setForm({ ...form, order_code: e.target.value })} placeholder="ORD-0032" data-testid="expense-order" /></div>
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="expense-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="toll">Toll</SelectItem>
                    <SelectItem value="food">Food</SelectItem>
                    <SelectItem value="parking">Parking</SelectItem>
                    <SelectItem value="repair">Repair</SelectItem>
                    <SelectItem value="fuel">Fuel</SelectItem>
                    <SelectItem value="misc">Miscellaneous</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Amount (₹) <span className="text-status-cancelled">*</span></Label><Input type="number" step="0.01" value={form.amount || ""} onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })} data-testid="expense-amount" /></div>
              <div>
                <Label>Payment mode</Label>
                <Select value={form.payment_mode} onValueChange={(v) => setForm({ ...form, payment_mode: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="company_card">Company Card</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Remarks</Label><Textarea rows={2} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></div>
            </div>
            <DialogFooter><Button onClick={() => create.mutate()} disabled={form.amount <= 0 || create.isPending} data-testid="btn-save-expense">{create.isPending ? "Saving…" : "Submit"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent className="p-0">
        <table className="data-table w-full">
          <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="px-4 py-2">Code</th><th className="px-4 py-2">Date</th>
              <th className="px-4 py-2">Order</th><th className="px-4 py-2">Driver</th>
              <th className="px-4 py-2">Category</th><th className="px-4 py-2 text-right">Amount</th>
              <th className="px-4 py-2">Payment</th><th className="px-4 py-2">Status</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((e) => (
              <tr key={e.id} className="border-b border-border/40 text-sm" data-testid={`exp-row-${e.code}`}>
                <td className="px-4 py-2 font-mono">{e.code}</td>
                <td className="px-4 py-2 text-xs">{e.date?.slice(0, 10)}</td>
                <td className="px-4 py-2 font-mono text-xs">{e.order_code || "—"}</td>
                <td className="px-4 py-2 text-xs">{e.driver_name || "—"}</td>
                <td className="px-4 py-2 text-xs capitalize">{e.category}</td>
                <td className="px-4 py-2 text-right font-mono font-semibold">{money(e.amount)}</td>
                <td className="px-4 py-2 text-xs capitalize">{e.payment_mode?.replace("_", " ")}</td>
                <td className="px-4 py-2"><Badge className={STATUS_STYLES[e.status] || ""} data-testid={`exp-status-${e.code}`}>{e.status}</Badge></td>
                <td className="px-4 py-2 text-right">
                  {isPrivileged && e.status === "submitted" && (
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="outline" onClick={() => status.mutate({ id: e.id, status: "approved" })} data-testid={`exp-approve-${e.code}`}><Check size={12} className="mr-1" /> Approve</Button>
                      <Button size="sm" variant="outline" onClick={() => status.mutate({ id: e.id, status: "rejected" })} data-testid={`exp-reject-${e.code}`}><X size={12} className="mr-1" /> Reject</Button>
                    </div>
                  )}
                  {isPrivileged && e.status === "approved" && (
                    <Button size="sm" variant="outline" onClick={() => status.mutate({ id: e.id, status: "reimbursed" })} data-testid={`exp-reimburse-${e.code}`}><IndianRupee size={12} className="mr-1" /> Mark reimbursed</Button>
                  )}
                  {canDelete && (
                    <Button size="sm" variant="ghost" onClick={() => del.mutate(e.id)} data-testid={`exp-del-${e.code}`}><X size={12} /></Button>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-sm text-muted-foreground">
                <Receipt size={32} className="mx-auto mb-2 opacity-40" />
                No expenses yet. Drivers can add entries from the road.
              </td></tr>
            )}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function ExpensesReport() {
  const { data } = useQuery({ queryKey: ["expense-report"], queryFn: () => api.get("/expenses/report").then((r) => r.data) });
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Kpi icon={Receipt} label="Entries" value={data?.total_entries || 0} />
        <Kpi icon={IndianRupee} label="Pending" value={money(data?.total_pending)} />
        <Kpi icon={IndianRupee} label="Approved" value={money(data?.total_approved)} />
        <Kpi icon={IndianRupee} label="Reimbursed" value={money(data?.total_reimbursed)} />
      </div>
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Users size={14} /> By driver</CardTitle></CardHeader>
        <CardContent className="p-0">
          <ReportTable rows={data?.by_driver || []} keyField="driver_id" labelField="driver_name" />
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle>By category</CardTitle></CardHeader>
        <CardContent className="p-0">
          <ReportTable rows={data?.by_category || []} keyField="category" labelField="category" />
        </CardContent>
      </Card>
    </div>
  );
}

function ReportTable({ rows, keyField, labelField }) {
  return (
    <table className="data-table w-full">
      <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
        <tr><th className="px-4 py-2">Name</th><th className="px-4 py-2 text-right">Entries</th><th className="px-4 py-2 text-right">Total</th></tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r[keyField]} className="border-b border-border/40 text-sm">
            <td className="px-4 py-2 capitalize">{r[labelField]}</td>
            <td className="px-4 py-2 text-right font-mono">{r.count}</td>
            <td className="px-4 py-2 text-right font-mono font-semibold">{money(r.amount)}</td>
          </tr>
        ))}
        {rows.length === 0 && <tr><td colSpan={3} className="px-4 py-8 text-center text-muted-foreground">No data yet.</td></tr>}
      </tbody>
    </table>
  );
}

const Kpi = ({ icon: Icon, label, value }) => (
  <div className="kpi-card">
    <div className="flex items-center justify-between text-muted-foreground">
      <div className="text-xs uppercase tracking-widest">{label}</div>
      <Icon size={14} />
    </div>
    <div className="mt-2 font-display text-xl font-black">{value}</div>
  </div>
);
